import React, { useEffect, useMemo, useState } from 'react';

import { MenuItem, Theme, Typography } from '@mui/material';
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
} from '@mui/material/Autocomplete';
import Skeleton from '@mui/material/Skeleton';
import makeStyles from '@mui/styles/makeStyles';
import { AxiosResponse } from 'axios';
import { FormikProps } from 'formik';
import querystring from 'querystring';
import { useRecoilValueLoadable } from 'recoil';
import {
  FormikSelect,
  MaterialAutocomplete,
  TIndexableObject,
} from '../../../common/formik-material-ui';
import { loadableEnum } from '../../../common/useMakePHPRequest';
import {
  makePHPRequest,
  methodNameEnum,
} from '../../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';

const useStyles = makeStyles((theme: Theme) => ({
  actionFormContainer: {
    margin: 10, // container margin
  },
  actionFormGroup: {
    display: 'flex', // allow form elements to wrap
  },
  actionDropdown: {
    margin: '0 10px', // left/right margin on dropdown
  },
  actionSubmit: {
    margin: '0 10px', // left/right margin on submit
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    // TODO: move to theme
    '& .MuiFormHelperText-root': {
      marginLeft: 0, // adjust helper text
    },
    '& .MuiInputLabel-root[data-shrink=false]': {
      opacity: 0.5, // make placeholder lighter
    },
  },
  skeleton: {
    backgroundColor: 'lightgrey',
  },
}));

export const capitalize = (word: string) =>
  word.charAt(0).toUpperCase() + word.slice(1);

export const DefaultActionSkeleton = () => {
  const classes = useStyles();
  return (
    <div>
      <Typography component="div" variant={'h3'}>
        <Skeleton className={classes.skeleton} />
      </Typography>
      <Typography component="div" variant={'h3'}>
        <Skeleton className={classes.skeleton} />
      </Typography>
    </div>
  );
};

/**
 * Parse defaultAction payload and convert to url
 * @see https://stackoverflow.com/questions/57086672/element-implicitly-has-an-any-type-because-expression-of-type-string-cant-b
 * @param defaultActionPayload Payload from react-final-form
 */
export const getDefaultActionUrl = (defaultActionPayload: {
  /** Default action type */
  type: IActionType;
  /** Type of component this will be used in */
  parentType: 'group' | 'ivr' | 'user' | 'queue' | 'routing';
  /** Id of the entity (eg. queueid, groupid) since voicemail will be triggered from that entity */
  voicemail: string;
  ivr?: string;
  queue?: string;
  group?: string;
  user?: string;
  businessid?: string | number;
}) => {
  let defaultActionUrl: string | null;
  // get default action Id if payload exists
  // defaultActionPayload.type contains name of key in IGetDefaultActionUrl
  const defaultActionId = (defaultActionPayload as Record<string, any>)[
    defaultActionPayload.type
  ];

  // use type if type defaultActionId is not null/undefined
  // voicemail does not have id
  switch (defaultActionPayload.type) {
    case 'voicemail':
      defaultActionUrl =
        `${process.env.VITE_APP_PHP_LIVE}/scripts/plivo/modules/voicemail.php?` +
        querystring.stringify({
          businessid: defaultActionPayload.businessid || 995,
          id: defaultActionId,
          type: defaultActionPayload.parentType,
        });
      break;
    case 'ivr':
      defaultActionUrl =
        `${process.env.VITE_APP_PHP_LIVE}/scripts/plivo/modules/business-ivr.php?` +
        querystring.stringify({
          businessid: defaultActionPayload.businessid || 995,
          ivrid: defaultActionId,
        });
      break;
    case 'group':
      defaultActionUrl =
        `${process.env.VITE_APP_PHP_LIVE}/scripts/plivo/modules/group.php?` +
        querystring.stringify({
          groupid: defaultActionId,
        });
      break;
    case 'user':
      defaultActionUrl =
        `${process.env.VITE_APP_PHP_LIVE}/scripts/plivo/modules/user.php?` +
        querystring.stringify({
          userid: defaultActionId,
        });
      break;
    case 'queue':
      defaultActionUrl =
        `${process.env.VITE_APP_PHP_LIVE}/scripts/plivo/modules/queueCall.php?` +
        querystring.stringify({
          businessid: defaultActionPayload.businessid || 995,
          queueid: defaultActionId,
        });
      break;
    case 'hangup':
      defaultActionUrl =
        `${process.env.VITE_APP_PHP_LIVE}/scripts/plivo/static/hangup.html?` +
        querystring.stringify({
          a: 'a',
        });
      break;
    case 'owner':
      defaultActionUrl = `${process.env.VITE_APP_PHP_LIVE}/scripts/plivo/modules/user.php?`;
      break;
    case 'dealOwner':
      defaultActionUrl = `${process.env.VITE_APP_PHP_LIVE}/scripts/plivo/modules/user.php?`;
      break;
    default:
      defaultActionUrl = ''; // cascade to business or hang up
      break;
  }

  return defaultActionUrl;
};

interface IGetDeafultObjectAndValue {
  defaultType: IActionType;
  ivrOptions: Record<string, any>[];
  ivrDefaultObject: Record<string, any>;
  ivrDefaultValue: string;
  groupOptions: Record<string, any>[];
  groupDefaultObject: Record<string, any>;
  groupDefaultValue: string;
  userOptions: Record<string, any>[];
  userDefaultObject: Record<string, any>[];
  userDefaultValue: string;
  queueOptions: Record<string, any>[];
  queueDefaultObject: Record<string, any>;
  queueDefaultValue: string;
  loadableState: loadableEnum;
}

/**
 * Get options and defaults for dropdown based on defaultAction URL
 *
 * @param defaultAction Url from queue object retreived from server
 * @returns Object containing all dropdown settings
 */
export const getDefaultActionFields = (
  defaultAction: string | undefined,
  showVoicemail: boolean,
  showOwner?: boolean,
  id?: string
): IGetDeafultObjectAndValue => {
  const businessQueuesLoadable = useRecoilValueLoadable<AxiosResponse>(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_QUEUES })
  );
  const businessIVRsLoadable = useRecoilValueLoadable<AxiosResponse>(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_IVRS })
  );
  // const businessGroupsLoadable = useRecoilValueLoadable<AxiosResponse>(
  //   makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_GROUPS })
  // );
  const dataRingGroups = {
    call: methodNameEnum.GET_BUSINESS_GROUPS,
  };
  const businessGroupsLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_GROUPS,
      payloadJsonString: JSON.stringify(dataRingGroups),
    })
  );
  // const businessUsersLoadable = useRecoilValueLoadable(
  //   makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_USERS })
  // );
  const data = {
    call: methodNameEnum.GET_BUSINESS_USERS,
  };
  const businessUsersLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify(data),
    })
  );

  return useMemo(() => {
    let loadableState: loadableEnum = loadableEnum.LOADING;
    // set combined state into loadableState
    if (
      businessQueuesLoadable.state === 'loading' ||
      businessIVRsLoadable.state === 'loading' ||
      businessGroupsLoadable.state === 'loading' ||
      businessUsersLoadable.state === 'loading'
    ) {
      loadableState = loadableEnum.LOADING;
    } else if (
      businessQueuesLoadable.state === 'hasError' ||
      businessIVRsLoadable.state === 'hasError' ||
      businessGroupsLoadable.state === 'hasError' ||
      businessUsersLoadable.state === 'hasError'
    ) {
      loadableState = loadableEnum.HAS_ERROR;
    } else {
      loadableState = loadableEnum.HAS_VALUE;
    }

    let ivrOptions =
      (businessIVRsLoadable.contents as AxiosResponse)?.data?.result ?? [];
    const ivrSortArr = [...ivrOptions];
    ivrOptions = ivrSortArr.sort((a, b) => {
      try {
        return a?.IVRname?.toLowerCase().localeCompare(
          b?.IVRname.toLowerCase()
        );
      } catch (e) {
        return 0;
      }
    });
    if (id) {
      ivrOptions = ivrOptions.filter((ivrObj) => {
        return ivrObj.mainIVRid !== parseInt(id);
      });
    }
    let ivrDefaultObject = ivrOptions?.[0];

    let groupOptions =
      (businessGroupsLoadable.contents as any)?.response?.data?.result ?? [];
    const groupSortArr = [...groupOptions];
    groupOptions = groupSortArr.sort((a, b) => {
      try {
        return a?.groupname
          ?.toLowerCase()
          .localeCompare(b?.groupname.toLowerCase());
      } catch (e) {
        return 0;
      }
    });
    if (id) {
      groupOptions = groupOptions.filter((groupObj) => {
        return groupObj.groupid !== parseInt(id);
      });
    }
    let groupDefaultObject = groupOptions?.[0];

    let userOptions =
      (businessUsersLoadable.contents as any)?.response?.data?.result ?? [];
    const userSortArr = [...userOptions];
    userOptions = userSortArr.sort((a, b) => {
      try {
        return (a?.fname + a?.lname)
          ?.toLowerCase()
          .localeCompare((b?.fname + b?.lname)?.toLowerCase());
      } catch (e) {
        return 0;
      }
    });
    if (id) {
      userOptions = userOptions.filter((userObj) => {
        return userObj.userid !== parseInt(id);
      });
    }
    let userDefaultObject = userOptions?.[0];

    let queueOptions =
      (businessQueuesLoadable.contents as AxiosResponse)?.data?.result ?? [];
    const queueSortArr = [...queueOptions];
    queueOptions = queueSortArr.sort((a, b) => {
      try {
        return a?.queuename
          ?.toLowerCase()
          .localeCompare(b?.queuename.toLowerCase());
      } catch (e) {
        return 0;
      }
    });
    if (id) {
      queueOptions = queueOptions.filter((queueObj) => {
        return queueObj.queueid !== id;
      });
    }
    let queueDefaultObject = queueOptions?.[0];

    // set as one of these valid options
    let defaultType: IActionType;
    let defaultTypeValue: string | string[] | undefined | any;

    // get type and id from url
    // override default object/value
    let searchParams: URLSearchParams | undefined;
    try {
      searchParams = defaultAction
        ? new URL(defaultAction).searchParams
        : undefined;
    } catch (exception: any) {
      // eslint-disable-next-line no-console
      console.error(
        'DefaultAxtion.tsx: url =',
        defaultAction,
        exception.message
      );
    }

    if (searchParams?.has('ivrid')) {
      defaultType = 'ivr';
      defaultTypeValue = searchParams.get('ivrid');
      // if not found set to first
      ivrDefaultObject =
        ivrOptions.find(
          (option: any) => +option.mainIVRid === +defaultTypeValue
        ) ?? ivrOptions?.[0];
    } else if (searchParams?.has('groupid')) {
      defaultType = 'group';
      defaultTypeValue = searchParams.get('groupid');
      // if not found set to first
      groupDefaultObject =
        groupOptions?.find(
          (option: any) => +option.maingroupid === +defaultTypeValue
        ) ?? groupOptions?.[0];
    } else if (searchParams?.has('queueid')) {
      defaultType = 'queue';
      defaultTypeValue = searchParams.get('queueid');
      // if not found set to first
      queueDefaultObject =
        queueOptions.find(
          (option: any) => +option.queueid === +defaultTypeValue
        ) ?? queueOptions?.[0];
    } else if (searchParams?.has('userid')) {
      defaultType = 'user';
      defaultTypeValue = searchParams.get('userid');
      // if not found set to first
      userDefaultObject =
        userOptions.find(
          (option: any) => +option.userid === +defaultTypeValue
        ) ?? userOptions?.[0];
    } else if (searchParams?.has('a')) {
      defaultType = 'hangup';
    } else if (searchParams?.has('id')) {
      defaultType = 'voicemail';
      defaultTypeValue = searchParams?.get('id');
    } else {
      // default to voicemail unless show voicemail is disabled
      if (showOwner) {
        defaultType = 'owner';
      }
      if (showVoicemail) {
        defaultType = 'voicemail';
        defaultTypeValue = searchParams?.get('id');
      } else {
        defaultType = 'hangup';
      }
    }

    const ivrDefaultValue = ivrDefaultObject?.mainIVRid;
    const groupDefaultValue = groupDefaultObject?.maingroupid;
    const userDefaultValue = userDefaultObject?.userid;
    const queueDefaultValue = queueDefaultObject?.queueid;

    return {
      defaultType,
      ivrOptions,
      ivrDefaultObject,
      ivrDefaultValue,
      groupOptions,
      groupDefaultObject,
      groupDefaultValue,
      userOptions,
      userDefaultObject,
      userDefaultValue,
      queueOptions,
      queueDefaultObject,
      queueDefaultValue,
      loadableState,
    };
  }, [
    defaultAction,
    businessQueuesLoadable,
    businessIVRsLoadable,
    businessGroupsLoadable,
    businessUsersLoadable,
    id,
  ]);
};

/**
 * Parse default-action url into individual componets so they can be edited in the form
 */
export function getDefaultActionPayload2(props: {
  defaultAction: string | undefined;
  showVoicemail?: boolean;
  showOwner?: boolean;
  showDealOwner?: boolean;
  id?: string;
}) {
  let searchParams: URLSearchParams | undefined;
  try {
    searchParams = props.defaultAction
      ? new URL(props.defaultAction).searchParams
      : undefined;
  } catch (exception: any) {
    // eslint-disable-next-line no-console
    console.error(
      'DefaultAxtion.tsx: url =',
      props.defaultAction,
      exception.message
    );
  }

  if (searchParams?.has('ivrid')) {
    return {
      type: 'ivr' as IActionType,
      ivr: searchParams.get('ivrid') ?? undefined,
      url: props.defaultAction,
    };
  } else if (searchParams?.has('groupid')) {
    return {
      type: 'group' as IActionType,
      group: searchParams.get('groupid') ?? undefined,
      url: props.defaultAction,
    };
  } else if (searchParams?.has('queueid')) {
    return {
      type: 'queue' as IActionType,
      queue: searchParams.get('queueid') ?? undefined,
      url: props.defaultAction,
    };
  } else if (searchParams?.has('userid')) {
    return {
      type: 'user' as IActionType,
      user: searchParams.get('userid') ?? undefined,
      url: props.defaultAction,
    };
  } else if (searchParams?.has('a')) {
    return {
      type: 'hangup' as IActionType,
      url: props.defaultAction,
    };
  } else if (searchParams?.has('id')) {
    return {
      type: 'voicemail' as IActionType,
      subtype: searchParams.get('type') ?? undefined,
      businessId: searchParams.get('businessid') ?? undefined,
      url: props.defaultAction,
    };
  } else {
    // default to voicemail unless show voicemail is disabled
    if (props.showVoicemail) {
      return {
        type: 'voicemail' as IActionType,
        url: props.defaultAction,
      };
    } else if (props.showOwner) {
      return {
        type: 'owner' as IActionType,
        url: props.defaultAction,
      };
    } else if (props.showDealOwner) {
      return {
        type: 'dealOwner' as IActionType,
        url: props.defaultAction,
      };
    } else {
      return {
        type: 'hangup' as IActionType,
        url: props.defaultAction,
      };
    }
  }
}

export const getDefaultActionPayload = (
  defaultAction: string | undefined,
  showVoicemail: boolean,
  showOwner?: boolean,
  id?: string
) => {
  const {
    defaultType: type,
    ivrDefaultValue: ivr,
    groupDefaultValue: group,
    userDefaultValue: user,
    queueDefaultValue: queue,
  } = getDefaultActionFields(defaultAction, showVoicemail, showOwner, id);
  return {
    type,
    ivr,
    group,
    user,
    queue,
  };
};

export type IActionType =
  | 'type'
  | 'ivr'
  | 'group'
  | 'user'
  | 'queue'
  | 'voicemail'
  | 'hangup'
  | 'cascade'
  | 'owner'
  | 'dealOwner';

interface IDefaultAction {
  /** Label on outline */
  label?: string;
  /** Label for Go to Business */
  labelForBusiness?: string;
  /** default action type */
  actionType: IActionType;
  /** Formik fieldname */
  typeName: string;
  /** Formik fieldname */
  ivrName: string;
  /** Formik fieldname */
  groupName: string;
  /** Formik fieldname */
  userName: string;
  /** Formik fieldname */
  queueName: string;
  /** Formik properties  */
  formik: FormikProps<any>;
  /** className for styling */
  className?: string;
  /** Url that determines the default action (ie. dropdown values) */
  defaultAction: string | undefined;
  /** ID of the current object */
  id?: string;
  /** Prevent form update so formik.dirty can be read correctly (values from getDefaultActionFields()) */
  disableFormUpdate?: boolean;
  /**optional flag to hide voicemail option */
  showVoicemail?: boolean;
  /**optional flag to hide none option */
  // showCascade?: boolean;
  showOwner?: boolean;
  showDealOwner?: boolean;
  /** the name of the formik field for showing formik errors */
  formikField?: string;
  /** ignore setting the default autocomplete value whenever type changes */
  ignoreDefaultAutocomplete?: boolean;
  /** restrict which action types are shown in the dropdown */
  allowedTypes?: IActionType[];
}

export const DefaultAction = ({
  label = 'No Answer Action',
  labelForBusiness,
  actionType,
  typeName,
  ivrName,
  groupName,
  userName,
  queueName,
  formik,
  className,
  defaultAction,
  disableFormUpdate,
  id,
  showVoicemail = true,
  showOwner = false,
  formikField = '',
  ignoreDefaultAutocomplete = false,
  showDealOwner = false,
  allowedTypes,
}: // showCascade = true,
IDefaultAction) => {
  const classes = useStyles();

  // get all defaults based on queue
  const {
    defaultType,
    ivrOptions,
    ivrDefaultObject,
    ivrDefaultValue,
    groupOptions,
    groupDefaultObject,
    groupDefaultValue,
    userOptions,
    userDefaultObject,
    userDefaultValue,
    queueOptions,
    queueDefaultObject,
    queueDefaultValue,
    loadableState,
  } = getDefaultActionFields(defaultAction, showVoicemail, showOwner, id);

  /**
   * Use external state for autocomplete since it is incompatible with final-form
   * and allows onChangeForType to zero out autocomplete on change
   */
  const [autocomplete, setAutocomplete] = useState<any>({
    ivr: ivrDefaultObject,
    group: groupDefaultObject,
    user: userDefaultObject,
    queue: queueDefaultObject,
  });

  const defaultActionTypeToPropMap: TIndexableObject = {
    ivr: 'mainIVRid',
    queue: 'queueid',
    group: 'maingroupid',
    user: 'userid',
  };

  /**
   * Change field with new value
   * @param name Name of field (if nested then last value is type)
   */
  const onChangeForAutocomplete = (name: string) => (
    event: React.ChangeEvent<any>,
    newValue: any,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any>
  ) => {
    // get last value (make it work for non-nested fields)
    const type: string = name.split('.').slice(-1).pop() ?? '';

    // keep autocomplete in sync
    setAutocomplete({
      ...autocomplete,
      [type]: newValue, // update value for type
    });

    formik.handleChange({
      target: {
        name,
        value: newValue?.[defaultActionTypeToPropMap[type]],
      },
    });
  };

  useEffect(() => {
    if (actionType !== 'hangup') {
      if (ignoreDefaultAutocomplete) {
        if (formikField) {
          let typeId;
          switch (actionType) {
            case 'ivr':
              typeId = autocomplete?.ivr?.mainIVRid;
              break;
            case 'queue':
              typeId = autocomplete?.queue?.queueid;
              break;
            case 'group':
              typeId = autocomplete?.group?.maingroupid;
              break;
            case 'user':
              typeId = autocomplete?.user?.userid;
              break;
          }
          formik.setFieldValue(`${formikField}.type`, actionType);
          formik.setFieldValue(`${formikField}.${actionType}`, typeId);
        }
      }
    }
  }, [actionType]);

  // set initial values and update when new data arrives
  useEffect(() => {
    if (
      !disableFormUpdate &&
      // prevent from null out default values
      ((defaultType === 'ivr' && ivrDefaultValue) ||
        (defaultType === 'group' && groupDefaultValue) ||
        (defaultType === 'user' && userDefaultValue) ||
        (defaultType === 'queue' && queueDefaultValue))
    ) {
      formik.setFieldValue(typeName, defaultType);
      formik.setFieldValue(ivrName, ivrDefaultValue);
      formik.setFieldValue(groupName, groupDefaultValue);
      formik.setFieldValue(userName, userDefaultValue);
      formik.setFieldValue(queueName, queueDefaultValue);
    }

    // update autoclompelete with new data
    setAutocomplete({
      ivr: ivrDefaultObject,
      group: groupDefaultObject,
      user: userDefaultObject,
      queue: queueDefaultObject,
    });
  }, [
    defaultType,
    ivrDefaultValue,
    groupDefaultValue,
    userDefaultValue,
    queueDefaultValue,
  ]);

  return loadableState === loadableEnum.LOADING ? (
    <DefaultActionSkeleton />
  ) : (
    <fieldset className={className}>
      <FormikSelect
        name={typeName}
        className={classes.field}
        formik={formik}
        fullWidth
        label={label}
        size="small"
        variant="outlined"
        data-qa={'default-action-select'}
        onChange={(event: React.ChangeEvent<any>) => {
          if (
            event?.target?.value === 'dealOwner' ||
            event?.target?.value === 'owner'
          ) {
            formik.setFieldValue(`destinationMessage.showMessage`, true);
            if (formik?.values?.destinationMessage?.messageType === 'tts') {
              formik.setFieldValue(
                `destinationMessage.message`,
                'call will be re-routed'
              );
            }
          } else {
            formik.setFieldValue(`destinationMessage.showMessage`, false);
          }
        }}
        // defaultValue={defaultType}
      >
        {showVoicemail && (!allowedTypes || allowedTypes.includes('voicemail')) && (
          <MenuItem data-qa="default-action-voicemail" value="voicemail">
            Voicemail
          </MenuItem>
        )}
        {showOwner && (!allowedTypes || allowedTypes.includes('owner')) && (
          <MenuItem data-qa="default-action-owner" value="owner">
            Go to Owner
          </MenuItem>
        )}
        {showDealOwner && (!allowedTypes || allowedTypes.includes('dealOwner')) && (
          <MenuItem data-qa="default-action-dealOwner" value="dealOwner">
            Go to Deal Owner
          </MenuItem>
        )}
        {(!allowedTypes || allowedTypes.includes('ivr')) && (
          <MenuItem data-qa="default-action-ivr" value="ivr">
            Go to IVR
          </MenuItem>
        )}
        {(!allowedTypes || allowedTypes.includes('group')) && (
          <MenuItem data-qa="goto-group-option" value="group">
            Go to Group
          </MenuItem>
        )}
        {(!allowedTypes || allowedTypes.includes('user')) && (
          <MenuItem data-qa="goto-user-option" value="user">
            Go to Agent
          </MenuItem>
        )}
        {(!allowedTypes || allowedTypes.includes('queue')) && (
          <MenuItem data-qa="goto-queue-option" value="queue">
            Go to Queue
          </MenuItem>
        )}
        {(!allowedTypes || allowedTypes.includes('hangup')) && (
          <MenuItem data-qa="default-action-hangup" value="hangup">
            Hangup
          </MenuItem>
        )}
        {/* {showCascade && ( // to be done later
          <MenuItem value="cascade">
            <em>Go to Business "{labelForBusiness ?? label}"</em>
          </MenuItem>
        )} */}
      </FormikSelect>
      {actionType === 'ivr' && (
        <MaterialAutocomplete
          name={ivrName}
          type="text"
          fullWidth
          helperText={(formik.errors?.[formikField ?? ''] as any)?.ivr ?? ''}
          error={!!(formik.errors?.[formikField ?? ''] as any)?.ivr}
          label="IVR"
          size="small"
          variant="outlined"
          options={ivrOptions}
          value={autocomplete?.ivr ?? {}}
          onChange={onChangeForAutocomplete(ivrName)}
          getOptionLabel={(option: any) => option?.IVRname ?? ''}
          isOptionEqualToValue={(option: any, value: any) =>
            option?.mainIVRid === value?.mainIVRid
          }
          dataPup="default-action-ivr-autocomplete"
          renderOption={(props, option: any) => {
            const optionProps = { ...props, key: option.mainIVRid };
            return (
              <li
                {...optionProps}
                data-qa="default-action-ivr-autocomplete-item"
              >
                {option?.IVRname ?? ''}
              </li>
            );
          }}
        />
      )}
      {actionType === 'group' && (
        <MaterialAutocomplete
          name={groupName}
          type="text"
          fullWidth
          helperText={(formik.errors?.[formikField ?? ''] as any)?.group ?? ''}
          error={!!(formik.errors?.[formikField ?? ''] as any)?.group}
          label="Group"
          size="small"
          variant="outlined"
          options={groupOptions}
          value={autocomplete?.group ?? {}}
          onChange={onChangeForAutocomplete(groupName)}
          getOptionLabel={(option: any) => option?.groupname ?? ''}
          isOptionEqualToValue={(option: any, value: any) =>
            option?.maingroupid === value?.maingroupid
          }
          renderOption={(props, option: any) => {
            const optionProps = { ...props, key: option.maingroupid };
            return (
              <li
                {...optionProps}
                data-qa="default-action-group-autocomplete-item"
              >
                {option?.groupname ?? ''}
              </li>
            );
          }}
        />
      )}
      {actionType === 'user' && (
        <MaterialAutocomplete
          name={userName}
          type="text"
          fullWidth
          helperText={(formik.errors?.[formikField ?? ''] as any)?.user ?? ''}
          error={!!(formik.errors?.[formikField ?? ''] as any)?.user}
          label="Agent"
          size="small"
          variant="outlined"
          options={userOptions}
          value={autocomplete?.user ?? {}}
          onChange={onChangeForAutocomplete(userName)}
          getOptionLabel={(option: any) =>
            option?.fname ? `${option?.fname} ${option?.lname}` : ''
          } // collapse to empty string if no option found
          isOptionEqualToValue={(option: any, value: any) =>
            option?.userid === value?.userid
          }
          renderOption={(props, option: any) => {
            const optionProps = { ...props, key: option.userid };
            return (
              <li
                {...optionProps}
                data-qa="default-action-user-autocomplete-item"
              >
                {`${option?.fname} ${option?.lname}`}
              </li>
            );
          }}
        />
      )}
      {actionType === 'queue' && (
        <MaterialAutocomplete
          name={queueName}
          type="text"
          fullWidth
          helperText={(formik.errors?.[formikField ?? ''] as any)?.queue ?? ''}
          error={!!(formik.errors?.[formikField ?? ''] as any)?.queue}
          label="Queue"
          size="small"
          variant="outlined"
          options={queueOptions}
          value={autocomplete?.queue ?? {}}
          onChange={onChangeForAutocomplete(queueName)}
          getOptionLabel={(option: any) => option?.queuename ?? ''}
          isOptionEqualToValue={(option: any, value: any) =>
            option?.queueid === value?.queueid
          }
          renderOption={(props, option: any) => {
            const optionProps = { ...props, key: option.queueid };
            return (
              <li
                {...optionProps}
                data-qa="default-action-queue-autocomplete-item"
              >
                {option?.queuename ?? ''}
              </li>
            );
          }}
        />
      )}
    </fieldset>
  );
};
